import React from "react";
import "./MobileWorkspace.css";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import { Link } from "react-router";
import {
  AlertTriangle,
  CalendarDays,
  ClipboardList,
  Download,
  GitBranch,
  Search,
  Truck,
} from "lucide-react";
import { useToast } from "./ResultsPrimitives.jsx";
import useOrdersPageData from "../hooks/useOrdersPageData.js";
import { downloadOrdersRoundtrip } from "../lib/exportApi.js";
import {
  isSaveLocationCancelledError,
  prepareBrowserSaveTarget,
  SAVE_MODE_FILE_SYSTEM,
  XLSX_MEDIA_TYPE,
} from "../utils/browserSaveTarget.js";
import { getCropTone } from "../utils/cropColors.js";
import { formatTonnage } from "../utils/pageDataHelpers.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";

const ORDERS_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("orders");

function SummaryCard({ accentClassName, Icon, label, note, value }) {
  return (
    <article className="rounded-lg border border-slate-200/80 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.045)]">
      <div className="flex items-start justify-between gap-2.5">
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
          <p className="mt-1 text-[1.24rem] font-semibold leading-none tracking-[-0.03em] text-slate-950">{value}</p>
        </div>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-sm ${accentClassName}`}>
          <Icon size={15} />
        </span>
      </div>
      <p className="mt-1.5 text-[10.5px] leading-4 text-slate-500">{note}</p>
    </article>
  );
}

function StatusFilterButton({ count, isActive, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11.5px] font-medium transition-colors duration-150",
        isActive
          ? "border-legacy-teal bg-legacy-teal text-white"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900",
      ].join(" ")}
    >
      <span>{label}</span>
      <span className={[
        "inline-flex min-w-5.5 items-center justify-center rounded-full px-1.25 py-px text-[10px] font-semibold",
        isActive ? "bg-[#dff3f0] text-legacy-teal" : "bg-slate-100 text-slate-600",
      ].join(" ")}>
        {count}
      </span>
    </button>
  );
}

export default function OrdersPage({ initialReadModel = null }) {
  const { showToast } = useToast();
  const [isExportingRoundtrip, setIsExportingRoundtrip] = React.useState(false);
  const {
    filterCounts,
    filteredOrders,
    grainBreakdown,
    grainFilter,
    grainOptions,
    resultsHref,
    searchQuery,
    setGrainFilter,
    setStatusFilter,
    statusFilter,
    statusFilters,
    summary,
    updateSearchQuery,
    visibleTonnage,
  } = useOrdersPageData(initialReadModel);

  const handleOrdersRoundtripExport = async () => {
    if (isExportingRoundtrip) {
      return;
    }

    setIsExportingRoundtrip(true);
    try {
      const saveTarget = await prepareBrowserSaveTarget({
        fallbackFileName: "orders.xlsx",
        mediaType: XLSX_MEDIA_TYPE,
        promptForSaveLocation: true,
      });
      const download = await downloadOrdersRoundtrip({ saveTarget });
      showToast({
        duration: 5000,
        title: download?.saveMode === SAVE_MODE_FILE_SYSTEM ? "Fichier enregistré" : "Export téléchargé",
        message: download?.saveMode === SAVE_MODE_FILE_SYSTEM
          ? "Le workbook Orders round-trip a été enregistré à l’emplacement choisi."
          : "Le workbook Orders round-trip a été téléchargé.",
        type: "success",
      });
    } catch (error) {
      if (isSaveLocationCancelledError(error)) {
        showToast({
          duration: 4500,
          title: "Enregistrement annulé",
          message: "Aucun fichier n'a été enregistré.",
          type: "info",
        });
        return;
      }

      showToast({
        duration: 8000,
        title: "Export impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "Le workbook Orders n'a pas pu être exporté.",
        type: "error",
      });
    } finally {
      setIsExportingRoundtrip(false);
    }
  };

  return (
    <section data-mobile-workspace="orders" className={`mx-auto grid min-w-0 w-full ${ORDERS_SURFACE_WIDTH_CLASS_NAME} gap-2.5 px-4 pb-4 pt-0 max-[720px]:px-4`}>
      <WorkspacePageHeader icon={ClipboardList} title="Commandes" description="Consultez les commandes et leur état d’affectation." />
      <section className="flex flex-wrap items-center justify-between gap-2.5 rounded-[20px] border border-slate-200/80 bg-white px-3.5 py-2.5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-[11.5px] text-slate-500">
          <span className="rounded-full bg-slate-900 px-2.5 py-0.75 font-semibold text-white">{summary.total} lignes commandes</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.75 font-medium text-slate-600">{formatTonnage(summary.tonnage)} t visibles</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.75 font-medium text-slate-600">{summary.deliveries} livraisons</span>
          {grainBreakdown.length === 0 ? (
            <span className="rounded-full border border-dashed border-slate-200 px-2.5 py-0.75 text-[10.5px] text-slate-500">Aucun produit exploitable</span>
          ) : grainBreakdown.slice(0, 4).map((item) => {
            const cropTone = getCropTone(item.grainType);

            return (
              <span
                key={item.grainType}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.75 text-[10.5px] font-medium ${cropTone.badgeClassName}`}
              >
                <span className="max-w-35 truncate">{item.grainType}</span>
                <span className={`rounded-full px-1.25 py-px text-[9.5px] ${cropTone.countClassName}`}>{item.count}</span>
              </span>
            );
          })}
          {grainBreakdown.length > 4 ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-0.75 text-[10.5px] font-medium text-slate-600">+{grainBreakdown.length - 4} autres</span>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5 max-[720px]:w-full max-[720px]:min-w-0 max-[720px]:shrink">
          <button
            type="button"
            onClick={handleOrdersRoundtripExport}
            disabled={isExportingRoundtrip}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-[11.5px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-100"
          >
            <Download size={14} />
            {isExportingRoundtrip ? "Export en cours..." : "Exporter Commandes"}
          </button>

          <Link
            to={resultsHref}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-legacy-teal px-3.5 py-1.5 text-[11.5px] font-semibold text-white transition-colors duration-150 hover:bg-[#0c4b47]"
          >
            <GitBranch size={14} />
            Ouvrir Affectation
          </Link>
        </div>
      </section>

      <section className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          accentClassName="bg-[#dff3f0] text-legacy-teal"
          Icon={ClipboardList}
          label="Commandes"
          note={`${filteredOrders.length} visibles après filtrage`}
          value={String(summary.total)}
        />
        <SummaryCard
          accentClassName="bg-emerald-100 text-emerald-700"
          Icon={Truck}
          label="Affectées"
          note={`${summary.attention} avec attention routage ou chargement`}
          value={String(summary.assigned)}
        />
        <SummaryCard
          accentClassName="bg-amber-100 text-amber-700"
          Icon={AlertTriangle}
          label="À planifier"
          note={`${summary.pending} lignes sans camion retenu`}
          value={String(summary.pending)}
        />
        <SummaryCard
          accentClassName="bg-slate-100 text-slate-700"
          Icon={CalendarDays}
          label="Volume visible"
          note={`${filteredOrders.length} lignes dans la sélection courante`}
          value={`${formatTonnage(visibleTonnage)} t`}
        />
      </section>

      <section className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_20px_52px_rgba(15,23,42,0.055)]">
        <div className="border-b border-slate-200/80 px-3.5 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sélection courante</p>
              <h3 className="mt-0.5 text-[1rem] font-semibold text-slate-950">Commandes filtrables</h3>
            </div>
            <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
              {filteredOrders.length} ligne{filteredOrders.length > 1 ? "s" : ""} affichée{filteredOrders.length > 1 ? "s" : ""}
            </div>
          </div>

          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {statusFilters.map((filter) => (
              <StatusFilterButton
                key={filter.key}
                count={filterCounts[filter.key] || 0}
                isActive={statusFilter === filter.key}
                label={filter.label}
                onClick={() => setStatusFilter(filter.key)}
              />
            ))}
          </div>

          <div className="mt-2.5 grid gap-2 lg:grid-cols-[minmax(0,1fr)_200px]">
            <label className="grid gap-0.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              <span className="sr-only lg:not-sr-only">Recherche</span>
              <div className="relative block">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => updateSearchQuery(event.target.value)}
                  placeholder="Rechercher par ordre, client, chargement, produit..."
                  className="w-full rounded-[14px] border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-[12.5px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-legacy-teal focus:bg-white"
                />
              </div>
            </label>

            <label className="grid gap-0.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Produit
              <select
                value={grainFilter}
                onChange={(event) => setGrainFilter(event.target.value)}
                className="rounded-[14px] border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] font-medium normal-case tracking-normal text-slate-700 outline-hidden transition-colors duration-150 focus:border-legacy-teal focus:bg-white"
              >
                {grainOptions.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="hidden border-b border-slate-200/80 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 lg:grid lg:grid-cols-[116px_minmax(0,1fr)_minmax(0,0.92fr)_66px_minmax(200px,1.14fr)] lg:gap-2.5">
          <span>Commande</span>
          <span>Client / livraison</span>
          <span>Chargement / produit / code produit</span>
          <span>Volume</span>
          <span>Statut</span>
        </div>

        <div className="grid divide-y divide-slate-200/80">
          {filteredOrders.length === 0 ? (
            <div className="px-4 py-6 text-center text-[12.5px] text-slate-500">
              Aucun ordre ne correspond au filtre courant.
            </div>
          ) : filteredOrders.map((order) => {
            const cropTone = getCropTone(order.grainType);

            return (
              <article
                key={order.key}
                className="grid gap-2 px-3 py-2 transition-colors duration-150 hover:bg-slate-50 lg:grid-cols-[116px_minmax(0,1fr)_minmax(0,0.92fr)_66px_minmax(200px,1.14fr)] lg:gap-2.5"
              >
                <div className="min-w-0">
                  <p className="text-[12.5px] font-semibold text-slate-950">{order.orderId}</p>
                  <p className="mt-0.5 text-[10.5px] leading-4 text-slate-500">{order.workOrderNumber ? `WO ${order.workOrderNumber}` : "Sans numéro de travail"}</p>
                </div>

                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-medium text-slate-900">{order.clientName}</p>
                  <p title={order.address} className="mt-0.5 truncate text-[10.5px] leading-4 text-slate-500">{order.address}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-slate-500">
                    <span className="rounded-full bg-slate-100 px-1.75 py-0.5">{order.deliveryCount} livraison{order.deliveryCount > 1 ? "s" : ""}</span>
                    <span className="rounded-full bg-slate-100 px-1.75 py-0.5">{order.deliveryDateLabel}</span>
                  </div>
                </div>

                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-medium text-slate-900">{order.loadingName}</p>
                  <p title={order.loadingAddress} className="mt-0.5 truncate text-[10.5px] leading-4 text-slate-500">{order.loadingAddress}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-slate-500">
                    <span className={`rounded-full border px-1.75 py-0.5 font-medium ${cropTone.badgeClassName}`}>{order.grainType}</span>
                    {order.grainCode ? (
                      <span
                        className="rounded-full border border-slate-200 bg-white px-1.75 py-0.5 font-medium text-slate-600"
                        title={`Code produit : ${order.grainCode}`}
                      >
                        {order.grainCode}
                      </span>
                    ) : null}
                    {order.compatibilityCount > 0 ? (
                      <span className="rounded-full bg-slate-100 px-1.75 py-0.5">{order.compatibilityCount} camion{order.compatibilityCount > 1 ? "s" : ""} compatibles</span>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="rounded-full bg-slate-100 px-2 py-0.75 text-[10.5px] font-semibold text-slate-700">{order.quantityLabel}</span>
                </div>

                <div className="min-w-0">
                  <span className={`inline-flex rounded-full px-2 py-0.75 text-[10px] font-semibold ${order.status.accentClassName}`}>
                    {order.statusLabel}
                  </span>
                  <p className="mt-1 whitespace-pre-line text-[10.5px] leading-[1.35] text-slate-500">
                    {order.issueLines.join("\n")}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </section>
  );
}
